import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react-pro";
import { formatDateShort, formatDayMonth } from "../../lib/dates";
import CIcon from "@coreui/icons-react";
import { cilCreditCard, cilExternalLink } from "@coreui/icons";
import {
  type BillingContainerDto,
  type BillingLineDto,
  type BillingLineDiscountDto,
  type InvoiceDto,
  type PaymentMethodDto,
  billingIntervalLabel,
  type BillingWaitingYearlyDto,
  billingStatusColor,
  billingStatusLabel,
  cardBrandLabel,
  formatUsd,
  invoiceStatusColor,
  invoiceStatusLabel,
  paymentMethodTypeLabel,
  useBillingInvoices,
  useBillingPaymentMethod,
  useBillingSummary,
  useOpenBillingPortal,
} from "../../api/hooks/useBilling";
// Reused rather than re-derived, so a cancelled subscription is the same word and the same colour
// here as it is on My Subscriptions.
import { subscriptionStatusColor } from "../../api/hooks/useStrategySubscriptions";
import { addOnDetailsPath, membershipDetailsPath, routePaths } from "../../routes/route-paths";
import { discountLabel } from "../../lib/offer-copy";
import { UpdateCardButton } from "../../components/common/UpdateCardButton";
import { useTradingStrategies } from "../../api/hooks/useTradingStrategies";
import { StrategySideLabel } from "../trading-strategies/components/StrategySideLabel";

/**
 * A discount the customer is about to lose: when it stops, and what the bill goes up by. Null while
 * every discount on these lines runs on.
 *
 * Only the earliest end is described. Two offers ending on different dates make two rises, and the one
 * worth warning about is the one that happens first.
 *
 * Grouped by the date as printed, not by the instant. Two offers taken in the same checkout end seconds
 * apart, and bucketing on the timestamp would leave one of them out of a rise the page says happens on
 * its date — the sentence's number and its date have to come from the same set of offers.
 */
function nextDiscountStep(
  lines: BillingLineDto[]
): { endsAt: string; amountUsd: number; count: number } | null {
  const ending = lines
    .filter((line) => line.willRenew && line.discount?.endsAt)
    .map((line) => line.discount!);

  if (ending.length === 0) {
    return null;
  }

  const endsAt = ending.reduce(
    (earliest, discount) => (discount.endsAt! < earliest ? discount.endsAt! : earliest),
    ending[0].endsAt!
  );
  const onThatDate = ending.filter(
    (discount) => formatDateShort(discount.endsAt!) === formatDateShort(endsAt)
  );

  return {
    endsAt,
    amountUsd: onThatDate.reduce((sum, discount) => sum + discount.amountUsd, 0),
    count: onThatDate.length,
  };
}

/** What the renewing lines have taken off them, all together. */
function discountTotal(lines: BillingLineDto[]): number {
  return lines
    .filter((line) => line.willRenew)
    .reduce((sum, line) => sum + (line.discount?.amountUsd ?? 0), 0);
}

export function BillingLandingCoreui() {
  const { data, isLoading, error } = useBillingSummary();

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <CAlert color="danger">
        Unable to load your billing details. Please try again.
      </CAlert>
    );
  }

  const hasAnything =
    data.containers.length > 0 ||
    data.waitingYearlies.length > 0 ||
    data.staffManagedLines.length > 0;

  const unpaidDueDates = [
    ...data.containers
      .filter((container) => container.status === "PastDue")
      .map((container) => container.currentPeriodEnd),
    ...data.waitingYearlies
      .filter((waitingYearly) => waitingYearly.firstPaymentFailed)
      .map((waitingYearly) => waitingYearly.startsOn),
  ];

  return (
    <div>
      <div className="mb-4">
        <h2 className="mb-1">Billing</h2>
        <p className="text-body-secondary mb-0">
          What you pay, for what, and when — plus every invoice, ready to view
          or download.
        </p>
      </div>

      {unpaidDueDates.length > 0 && <PaymentFailedAlert dueDates={unpaidDueDates} />}

      {unpaidDueDates.length === 0 && data.nextPaymentDate !== null && data.nextPaymentAmountUsd !== null ? (
        <NextPaymentBand
          date={data.nextPaymentDate}
          amountUsd={data.nextPaymentAmountUsd}
          creditUsd={data.creditUsd}
          // Only the containers billing on that day. An annual falling due in March says nothing about
          // what is collected next week.
          lines={data.containers
            .filter((container) => container.currentPeriodEnd === data.nextPaymentDate)
            .flatMap((container) => container.lines)}
        />
      ) : (
        data.creditUsd > 0 && (
          <CCard className="mb-4">
            <CCardBody>
              <CreditOnAccount creditUsd={data.creditUsd} />
            </CCardBody>
          </CCard>
        )
      )}

      {!hasAnything ? (
        <CAlert color="info">
          You don't have any billed subscriptions yet.{" "}
          <Link to={routePaths.strategies}>Browse strategies</Link>.
        </CAlert>
      ) : (
        <CRow xs={{ gutter: 4 }}>
          {data.containers.map((container) => (
            <CCol
              key={`${container.interval}-${container.currentPeriodEnd}`}
              xl={6}
            >
              <BillingContainerCard container={container} />
            </CCol>
          ))}
          {data.waitingYearlies.map((waitingYearly) => (
            <CCol key={`waiting-${waitingYearly.tradingStrategyId}`} xl={6}>
              <WaitingYearlyCard waitingYearly={waitingYearly} />
            </CCol>
          ))}
          {data.staffManagedLines.length > 0 && (
            <CCol xl={6}>
              <StaffManagedCard lines={data.staffManagedLines} />
            </CCol>
          )}
        </CRow>
      )}

      {data.hasBillingAccount && (
        <>
          <PaymentMethodSection />
          <InvoicesSection />
        </>
      )}
    </div>
  );
}

/**
 * One card, one Manage button — deliberately not one per subscription. We set no payment method on the
 * subscriptions themselves, so they all bill the customer's default, and that is what this shows.
 *
 * Stripe's portal does let a customer pick a different card for an individual subscription, which writes
 * a subscription-level default and takes that subscription off the customer default. It's an explicit,
 * clearly-labelled choice on their side, so we honour it rather than fight it — but it's why this is
 * titled "Default payment method" and not "Payment method": once an override exists, this card is no
 * longer the whole story.
 */
function PaymentMethodSection() {
  const { data, isLoading } = useBillingPaymentMethod();

  return (
    <>
      <h5 className="mt-5 mb-3">Default payment method</h5>
      <CCard>
        <CCardBody className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
          {isLoading ? (
            <CSpinner size="sm" />
          ) : (
            <PaymentMethodOnFile paymentMethod={data?.paymentMethod ?? null} />
          )}
          <ManageBillingButton />
        </CCardBody>
      </CCard>
    </>
  );
}

/**
 * Only a bare card gives us brand, last4 and an expiry to print. A card saved through Link — the path
 * Stripe's checkout leads with — is charged just the same but keeps its details in the customer's Link
 * wallet, so all we can honestly show is what kind of method it is. That is still worth showing: telling
 * a paying customer there is no card on file reads as "your subscriptions are about to lapse".
 */
function PaymentMethodOnFile({
  paymentMethod,
}: {
  paymentMethod: PaymentMethodDto | null;
}) {
  if (paymentMethod === null) {
    return (
      <div className="text-body-secondary">
        No card on file. Add one to keep your subscriptions running.
      </div>
    );
  }

  const { card } = paymentMethod;

  return (
    <div className="d-flex align-items-center gap-3">
      <CIcon icon={cilCreditCard} size="xl" className="text-body-secondary" />
      <div>
        <div className="fw-semibold">
          {card === null ? (
            paymentMethodTypeLabel(paymentMethod.type)
          ) : (
            <>
              {cardBrandLabel(card.brand)} •••• {card.last4}
            </>
          )}
        </div>
        <div className="text-body-secondary small">
          {card === null
            ? "Use Manage billing to see or change it."
            : `Expires ${String(card.expiryMonth).padStart(2, "0")}/${card.expiryYear}`}
        </div>
      </div>
    </div>
  );
}

function InvoicesSection() {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBillingInvoices();

  const invoices = data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <h5 className="mt-5 mb-3">Invoices</h5>

      {isLoading ? (
        <div className="d-flex justify-content-center py-4">
          <CSpinner color="primary" />
        </div>
      ) : error ? (
        <CAlert color="danger">
          Unable to load your invoices. Please try again.
        </CAlert>
      ) : invoices.length === 0 ? (
        <CAlert color="info" className="mb-0">
          No invoices yet. Your first one appears here after your first payment.
        </CAlert>
      ) : (
        <CCard>
          <CTable align="middle" hover responsive className="mb-0">
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell scope="col">Date</CTableHeaderCell>
                <CTableHeaderCell scope="col">Amount</CTableHeaderCell>
                {/* The invoice number is the first thing to go on a narrow screen: it's reference
                    data, and the ••• menu still opens the invoice itself. Dropping it is what stops
                    the date wrapping to three lines. */}
                <CTableHeaderCell
                  scope="col"
                  className="d-none d-md-table-cell"
                >
                  Invoice
                </CTableHeaderCell>
                <CTableHeaderCell scope="col">Status</CTableHeaderCell>
                <CTableHeaderCell scope="col" className="text-end">
                  <span className="visually-hidden">Actions</span>
                </CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {invoices.map((invoice) => (
                <InvoiceRow key={invoice.id} invoice={invoice} />
              ))}
            </CTableBody>
          </CTable>

          {hasNextPage && (
            <div className="p-3 border-top">
              <CButton
                color="secondary"
                variant="outline"
                className="w-100"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? <CSpinner size="sm" /> : "Load more"}
              </CButton>
            </div>
          )}
        </CCard>
      )}
    </>
  );
}

function InvoiceRow({ invoice }: { invoice: InvoiceDto }) {
  // A draft invoice has no hosted page or PDF yet — Stripe only mints those on finalisation.
  const hasDocuments = invoice.viewUrl !== null || invoice.downloadUrl !== null;

  return (
    <CTableRow>
      <CTableDataCell className="text-nowrap">
        {formatDateShort(invoice.date)}
      </CTableDataCell>
      <CTableDataCell className="text-nowrap">
        {formatUsd(invoice.amountUsd)}
      </CTableDataCell>
      <CTableDataCell className="d-none d-md-table-cell text-body-secondary">
        {invoice.number ?? "—"}
      </CTableDataCell>
      <CTableDataCell>
        <CBadge color={invoiceStatusColor(invoice.status)} shape="rounded-pill">
          {invoiceStatusLabel(invoice.status)}
        </CBadge>
      </CTableDataCell>
      <CTableDataCell className="text-end">
        {hasDocuments && (
          <CDropdown alignment="end" variant="btn-group">
            <CDropdownToggle
              color="ghost"
              caret={false}
              aria-label={`Actions for invoice ${invoice.number ?? invoice.id}`}
            >
              •••
            </CDropdownToggle>
            <CDropdownMenu>
              {invoice.viewUrl !== null && (
                <CDropdownItem
                  href={invoice.viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View invoice
                </CDropdownItem>
              )}
              {invoice.downloadUrl !== null && (
                <CDropdownItem
                  href={invoice.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download invoice
                </CDropdownItem>
              )}
            </CDropdownMenu>
          </CDropdown>
        )}
      </CTableDataCell>
    </CTableRow>
  );
}

/**
 * Sends the customer to Stripe's Customer Portal in a new tab. The portal is a Stripe-hosted page and
 * cannot be framed, so this is a genuine hand-off rather than an in-app modal. The blank tab is opened
 * synchronously inside the click handler — opening it after the await would be swallowed by pop-up
 * blockers — and only then pointed at the minted URL.
 */
function ManageBillingButton() {
  const portal = useOpenBillingPortal();

  return (
    <div className="text-md-end">
      <CButton
        color="primary"
        onClick={portal.open}
        disabled={portal.isPending}
      >
        {portal.isPending && <CSpinner size="sm" className="me-2" />}
        Manage
        <CIcon icon={cilExternalLink} size="sm" className="ms-2 opacity-75" />
      </CButton>
      <div className="text-body-secondary small mt-2">
        Opens Stripe in a new tab
      </div>
      {portal.failed && (
        <CAlert color="danger" className="mt-2 mb-0 py-2 px-3 small">
          We couldn't open the billing portal. Please try again.
        </CAlert>
      )}
    </div>
  );
}

/**
 * Stands in for Next payment while a bill is unpaid: a failed bill isn't upcoming. One button however many
 * failed, since every subscription charges the account's card.
 */
function PaymentFailedAlert({ dueDates }: { dueDates: string[] }) {
  const count = dueDates.length;
  const firstDay = formatDayMonth(dueDates[0]);
  const sameDay = dueDates.every((dueDate) => formatDayMonth(dueDate) === firstDay);

  let payments = `${count} payments`;
  if (count === 1) {
    payments = "A payment";
  } else if (count === 2) {
    payments = "Two payments";
  }

  return (
    <CAlert
      color="danger"
      className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3"
    >
      <div style={{ flex: "1 1 300px", minWidth: 0 }}>
        <strong>
          {payments} didn't go through{sameDay ? ` on ${firstDay}` : ""}.
        </strong>{" "}
        Update your card and Stripe will try again.{" "}
        {count === 1
          ? "If it still can't be taken, the strategies it pays for will end."
          : "If they still can't be taken, the strategies they pay for will end."}
      </div>
      <UpdateCardButton />
    </CAlert>
  );
}

/** Stripe takes this off the next bills before the card, so it sits with the payment it lowers. */
function CreditOnAccount({ creditUsd }: { creditUsd: number }) {
  return (
    <div>
      <strong>Credit on your account: {formatUsd(creditUsd)}.</strong> It comes off your next payments
      first, for any strategy, and isn't paid out.
    </div>
  );
}

function NextPaymentBand({
  date,
  amountUsd,
  creditUsd,
  lines,
}: {
  date: string;
  amountUsd: number;
  creditUsd: number;
  lines: BillingLineDto[];
}) {
  const { t } = useTranslation("offers");
  const discounted = discountTotal(lines);
  const step = nextDiscountStep(lines);

  return (
    <CCard className="mb-4">
      <CCardBody>
        <div className="d-flex flex-wrap align-items-baseline gap-3">
          <div
            className="text-body-secondary text-uppercase small"
            style={{ letterSpacing: "0.06em" }}
          >
            Next payment
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.1 }}>
            {formatUsd(amountUsd)}
          </div>
          <div className="text-body-secondary">on {formatDateShort(date)}</div>
        </div>

        {discounted > 0 && (
          <div className="text-body-secondary small mt-2">
            {t("billing.nextPaymentIncludes", { amount: formatUsd(discounted) })}
            {step && (
              <>
                {" "}
                {t("billing.nextPaymentRises", {
                  count: step.count,
                  amount: formatUsd(amountUsd + step.amountUsd),
                  date: formatDateShort(step.endsAt),
                })}
              </>
            )}
          </div>
        )}

        {creditUsd > 0 && (
          <div className="mt-3 pt-3 border-top">
            <CreditOnAccount creditUsd={creditUsd} />
          </div>
        )}
      </CCardBody>
    </CCard>
  );
}

function BillingContainerCard({
  container,
}: {
  container: BillingContainerDto;
}) {
  const intervalLabel = billingIntervalLabel(container.interval);
  const cadence = container.interval === "Yearly" ? "year" : "month";

  // Whether anything further will be charged. Deliberately NOT read from cancelAtPeriodEnd alone:
  // that flag is only refreshed from Stripe on invoice.paid, and a subscription winding down never
  // produces another one — so it stays false forever and the card would claim it renews. Every line
  // being cancelled is the same fact, held locally and always current.
  const isEnding =
    container.cancelAtPeriodEnd ||
    (container.lines.length > 0 &&
      container.lines.every((line) => !line.willRenew));

  // One badge, in the same vocabulary My Subscriptions uses — a customer shouldn't meet two words for
  // one state. A payment problem outranks everything because it needs action; otherwise a wind-down
  // reads "Cancelled" exactly as it does on the subscription cards, and Cancelled beats Active there
  // too. Colour comes from the shared helper so the two pages can't drift apart.
  const needsAttention =
    container.status === "PastDue" || container.status === "Incomplete";
  const badge =
    !needsAttention && isEnding
      ? { label: "Cancelled", color: subscriptionStatusColor("Cancelled") }
      : {
          label: billingStatusLabel(container.status),
          color: billingStatusColor(container.status),
        };

  return (
    <CCard className="h-100">
      <CCardHeader className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <span>
          {intervalLabel}
          {container.interval === "Monthly" && container.lines.length > 1
            ? " bundle"
            : ""}
        </span>
        <CBadge color={badge.color} shape="rounded-pill">
          {badge.label}
        </CBadge>
      </CCardHeader>
      <CCardBody className="d-flex flex-column">
        {container.status === "PastDue" ? (
          <div className="text-danger fw-semibold small mb-3">
            {container.unpaidUsd !== null
              ? `${formatUsd(container.unpaidUsd)} due ${formatDateShort(container.currentPeriodEnd)} wasn't paid`
              : `The payment due ${formatDateShort(container.currentPeriodEnd)} wasn't paid`}
          </div>
        ) : (
          <div className="text-body-secondary small mb-3">
            {isEnding
              ? `Ends ${formatDateShort(container.currentPeriodEnd)} — no further charges`
              : `Renews ${formatDateShort(container.currentPeriodEnd)}`}
          </div>
        )}

        <ul className="list-unstyled mb-0">
          {container.lines.map((line) => (
            <BillingLineRow key={line.id} line={line} />
          ))}
        </ul>

        {container.recurringTotalUsd > 0 && (
          <ContainerTotal container={container} cadence={cadence} />
        )}
      </CCardBody>
    </CCard>
  );
}

/**
 * The card total, and — when a discount is running — what it costs now, plus the date it goes back up.
 * The rise is said under the total because the total is the figure people read first.
 */
function ContainerTotal({
  container,
  cadence,
}: {
  container: BillingContainerDto;
  cadence: string;
}) {
  const { t } = useTranslation("offers");
  const discounted = discountTotal(container.lines);
  const payable = container.recurringTotalUsd - discounted;
  const step = nextDiscountStep(container.lines);

  return (
    <div className="border-top mt-3 pt-3">
      <div className="d-flex justify-content-between align-items-baseline">
        <strong>Total per {cadence}</strong>
        <strong>
          {discounted > 0 && (
            <span className="text-body-secondary fw-normal text-decoration-line-through me-2">
              {formatUsd(container.recurringTotalUsd)}
            </span>
          )}
          {formatUsd(payable)}
        </strong>
      </div>
      {step && (
        <div className="text-body-secondary small mt-1">
          {t("billing.risesTo", {
            count: step.count,
            amount: formatUsd(payable + step.amountUsd),
            cadence,
            date: formatDateShort(step.endsAt),
          })}
        </div>
      )}
    </div>
  );
}

/** The side of a billed strategy, read from the catalogue since billing lines carry only its name. */
function HeldSide({ strategyId, className }: { strategyId: string | null; className?: string }) {
  const { data: strategies } = useTradingStrategies();
  const direction = strategies?.find((s) => s.id === strategyId)?.direction;

  return <StrategySideLabel direction={direction} className={className} />;
}

const MEMBERSHIP_NAMES = new Intl.ListFormat("en-GB", { style: "long", type: "conjunction" });

function BillingLineRow({ line }: { line: BillingLineDto }) {
  const discount = line.willRenew ? line.discount : null;

  return (
    <li className="d-flex justify-content-between align-items-baseline gap-3 py-2 border-bottom">
      <div>
        <Link
          to={
            line.membershipTierId
              ? membershipDetailsPath(line.membershipTierId)
              : line.addOnId
                ? addOnDetailsPath(line.addOnId)
                : `/subscriptions/${line.tradingStrategyId}`
          }
          className="text-decoration-none"
        >
          {line.strategyName}
        </Link>
        {!line.membershipTierId && !line.addOnId && (
          <HeldSide strategyId={line.tradingStrategyId} className="small ms-2" />
        )}
        {line.membershipTierId && (
          <CBadge color="secondary" shape="rounded-pill" className="ms-2">
            Membership
          </CBadge>
        )}
        {line.addOnId && (
          <CBadge color="secondary" shape="rounded-pill" className="ms-2">
            Add-on
          </CBadge>
        )}
        {line.yearlyFrom ? (
          <div className="text-body-secondary small">
            Yearly from {formatDayMonth(line.yearlyFrom)}
          </div>
        ) : (
          !line.willRenew && (
            <div className="text-body-secondary small">
              {line.activeTo
                ? `Cancelled — access until ${formatDateShort(line.activeTo)}`
                : "Cancelled"}
            </div>
          )
        )}
        {discount && <LineDiscountNote discount={discount} />}
        {line.includedInMemberships.length > 0 && line.willRenew && (
          <div className="text-warning-emphasis small">
            Also included in your {MEMBERSHIP_NAMES.format(line.includedInMemberships)}{" "}
            {line.includedInMemberships.length === 1 ? "membership" : "memberships"}. You can cancel it.
          </div>
        )}
      </div>
      <div
        className={`text-nowrap ${line.willRenew ? "" : "text-body-secondary text-decoration-line-through"}`}
      >
        {line.priceUsd === null ? (
          "—"
        ) : discount ? (
          <>
            <span className="text-body-secondary text-decoration-line-through me-2">
              {formatUsd(line.priceUsd)}
            </span>
            {formatUsd(Math.max(line.priceUsd - discount.amountUsd, 0))}
          </>
        ) : (
          formatUsd(line.priceUsd)
        )}
      </div>
    </li>
  );
}

function LineDiscountNote({ discount }: { discount: BillingLineDiscountDto }) {
  const { t } = useTranslation("offers");
  const terms = discountLabel(discount, t);

  return (
    <div className="text-success small">
      {discount.endsAt
        ? t("billing.lineDiscount", {
            offer: discount.offerName,
            discount: terms,
            date: formatDateShort(discount.endsAt),
          })
        : t("billing.lineDiscountOngoing", {
            offer: discount.offerName,
            discount: terms,
          })}
    </div>
  );
}

/**
 * Strategies granted by staff in the Back Office. They never appear on an invoice, so they're shown apart
 * from the paid containers rather than silently missing from a page titled "Billing".
 */
/**
 * A yearly subscription an upgrade has booked. It exists in Stripe already, so it has a card of its own
 * from the moment it's booked, carrying Stripe's figure for the first year.
 */
function WaitingYearlyCard({
  waitingYearly,
}: {
  waitingYearly: BillingWaitingYearlyDto;
}) {
  const firstPaymentUsd = waitingYearly.firstPaymentUsd ?? waitingYearly.priceUsd;
  const failed = waitingYearly.firstPaymentFailed;

  return (
    <CCard className="h-100">
      <CCardHeader className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <span>{billingIntervalLabel("Yearly")}</span>
        {failed ? (
          <CBadge color="danger" shape="rounded-pill">
            Payment failed
          </CBadge>
        ) : (
          <CBadge color="info" shape="rounded-pill">
            Starts {formatDayMonth(waitingYearly.startsOn)}
          </CBadge>
        )}
      </CCardHeader>
      <CCardBody className="d-flex flex-column">
        {failed ? (
          <div className="text-danger fw-semibold small mb-3">
            First payment of {formatUsd(firstPaymentUsd)} due {formatDateShort(waitingYearly.startsOn)} wasn't
            paid
          </div>
        ) : (
          <div className="text-body-secondary small mb-3">
            First payment {formatDateShort(waitingYearly.startsOn)}
          </div>
        )}

        <ul className="list-unstyled mb-0">
          <li className="d-flex justify-content-between align-items-baseline gap-3 py-2 border-bottom">
            <div>
              <Link
                to={`/subscriptions/${waitingYearly.tradingStrategyId}`}
                className="text-decoration-none"
              >
                {waitingYearly.strategyName}
              </Link>
              <HeldSide strategyId={waitingYearly.tradingStrategyId} className="small ms-2" />
            </div>
            <span className="text-nowrap">
              {firstPaymentUsd < waitingYearly.priceUsd && (
                <span className="text-body-secondary text-decoration-line-through me-2">
                  {formatUsd(waitingYearly.priceUsd)}
                </span>
              )}
              {formatUsd(firstPaymentUsd)}
            </span>
          </li>
        </ul>
      </CCardBody>
    </CCard>
  );
}

function StaffManagedCard({ lines }: { lines: BillingLineDto[] }) {
  return (
    <CCard className="h-100">
      <CCardHeader>Included at no charge</CCardHeader>
      <CCardBody>
        <div className="text-body-secondary small mb-3">
          Added to your account by Scryer — these aren't billed.
        </div>
        <ul className="list-unstyled mb-0">
          {lines.map((line) => (
            <li
              key={line.id}
              className="d-flex justify-content-between align-items-baseline gap-3 py-2 border-bottom"
            >
              <div>
                <Link
                  to={`/subscriptions/${line.tradingStrategyId}`}
                  className="text-decoration-none"
                >
                  {line.strategyName}
                </Link>
                <HeldSide strategyId={line.tradingStrategyId} className="small ms-2" />
              </div>
              <span className="text-body-secondary text-nowrap">No charge</span>
            </li>
          ))}
        </ul>
      </CCardBody>
    </CCard>
  );
}
